import React from 'react';
import { FileText, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import CustomSelect from '../base/CustomSelect';

export default function ReportDetails({
  data = [],
  pagination = { currentPage: 1, pageSize: 10, totalPages: 0, totalItems: 0 },
  onPageChange,
  loading = false,
  statusFilter = '',
  onStatusChange,
  statusOptions = [],
  onExport,
  exportLoading = false,
}) {
  const currentPage = pagination?.currentPage || 1;
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItems || 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">
            Chi tiết phản ánh
          </h3>
          {totalItems > 0 && (
            <span className="text-sm text-gray-500">({totalItems})</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <CustomSelect
            className="w-48"
            value={statusFilter}
            onChange={(val) => onStatusChange?.(typeof val === 'object' ? val?.target?.value : val)}
            placeholder="Tất cả trạng thái"
            options={[
              { value: "", label: "Tất cả trạng thái" },
              ...statusOptions.map((s) => ({ value: s, label: s }))
            ]}
          />

          <button
            onClick={() => onExport?.()}
            disabled={exportLoading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exportLoading ? 'Đang xuất...' : 'Xuất chi tiết'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[850px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Mã phản ánh
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Tiêu đề
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Nội dung
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Lĩnh vực
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Chuyên viên
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Trạng thái
              </th>
              <th className="text-left py-3 px-2 text-base font-bold text-gray-700">
                Ngày cập nhật
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  Không có dữ liệu để hiển thị
                </td>
              </tr>
            ) : (
              data.map((report, index) => (
                <tr
                  key={report.id || index}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                >
                  <td className="py-3 px-2 text-sm font-medium text-gray-900">
                    {report.id}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-900 max-w-xs">
                    <div className="truncate" title={report.title}>
                      {report.title}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600 max-w-xs">
                    <div className="truncate" title={report.description}>
                      {report.description || '—'}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600">
                    {report.category}
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600">
                    {report.assignee || '—'}
                  </td>
                  <td className="py-3 px-2">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: report.statusBg,
                        color: report.statusColor
                      }}
                    >
                      {report.status}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-sm text-gray-600">
                    {report.createdDate}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <span className="text-sm text-gray-500">
            Trang {currentPage}/{totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Trước
            </button>
            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sau <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
